import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { standupApi } from "@/lib/standup-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  PaperPlaneTilt, ArrowRight, Check, House, Link as LinkIcon, Copy,
  Trophy, Warning, Clock, TrendUp, ListChecks, Cube, X as XIcon,
  ShareNetwork, PencilSimple, Plus,
} from "@phosphor-icons/react";
import { toast } from "sonner";

// /standup/session/:id/summary — Frames 4.1, 4.2, 4.3 (+ 2.0a/2.0b host vs
// participant perspective chips). The page is identical for everyone; only
// the chip and a couple of CTA differences switch on `caller_is_host`.
//
// Lands the user automatically here when the host clicks "End Meeting" or
// when a participant arrives after submission and the host has finalised.

export default function StandUpSummary() {
  const { sessionId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [redirectIn, setRedirectIn] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    standupApi.summary(sessionId)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load summary"));
  }, [user, sessionId]);

  // Auto-redirect (participants only) — Frame 4.1 banner shows a 30s countdown
  // to dashboard. Hosts stick around to share + edit so we only auto-redirect
  // when caller_is_host is false AND status is finished.
  useEffect(() => {
    if (!data || data.caller_is_host || data.status !== "finished") return;
    let n = 30;
    setRedirectIn(n);
    const id = setInterval(() => {
      n -= 1;
      setRedirectIn(n);
      if (n <= 0) {
        clearInterval(id);
        navigate("/dashboard");
      }
    }, 1000);
    return () => clearInterval(id);
  }, [data, navigate]);

  if (authLoading || !user) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (err) return <div className="min-h-screen grid place-items-center text-rose-500" data-testid="summary-error">{err}</div>;
  if (!data) return <div className="min-h-screen grid place-items-center text-slate-500">Loading summary…</div>;

  const s = data.summary || {};
  const isHost = !!data.caller_is_host;
  const finished = data.status === "finished";

  return (
    <div className="min-h-screen bg-[#FAFAFC]" data-testid="standup-summary">
      {/* Top banner (Frame 4.1) */}
      {finished && !isHost && (
        <div className="bg-gradient-to-r from-emerald-50 to-sky-50 border-b border-emerald-100" data-testid="finish-banner">
          <div className="max-w-5xl mx-auto px-6 py-2 flex items-center gap-4 text-xs">
            <Check size={14} weight="bold" className="text-emerald-600" />
            <span className="font-semibold text-slate-800">Great work! This standup has finished.</span>
            <span className="text-slate-500">Redirecting to Dashboard in <span className="font-mono text-rose-500">{redirectIn ?? 30}s…</span></span>
            <div className="ml-auto flex items-center gap-3">
              <button
                onClick={async () => {
                  try {
                    const r = await standupApi.emailMeSummary(sessionId);
                    if (r.data.ok) toast.success(`Summary emailed to ${r.data.sent_to}`);
                    else toast.message("Email queued — check your inbox shortly");
                  } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
                }}
                className="inline-flex items-center gap-1 text-slate-700 hover:text-slate-900 font-medium"
                data-testid="receive-updates-btn"
              >
                <PaperPlaneTilt size={11} weight="fill" /> Receive Updates
              </button>
              <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1 font-medium text-slate-700 hover:text-slate-900" data-testid="exit-dashboard-btn">
                <House size={11} weight="bold" /> Exit to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* Title + Host/Participant chip */}
        <div className="flex items-center justify-between">
          <div>
            <span className={`inline-flex items-center rounded-full text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 ${
              isHost ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"
            }`} data-testid="perspective-chip">
              {isHost ? "Host View" : "Participant View"}
            </span>
            <h1 className="mt-3 text-3xl font-bold text-slate-900">Meeting Summary</h1>
            <p className="mt-1 text-sm text-slate-500 max-w-xl">
              Everything your team covered, distilled. The raw notes purge after 30 days — share or download today if you want to keep them.
            </p>
          </div>
          {isHost && finished && (
            <div className="flex items-center gap-2">
              <ShareModal sessionId={sessionId} />
              <Button onClick={() => navigate("/dashboard")} className="h-9 bg-slate-950 hover:bg-slate-800 text-white text-xs" data-testid="done-dashboard-btn">
                <House size={11} weight="bold" className="mr-1" /> Done & Go To Dashboard
              </Button>
            </div>
          )}
        </div>

        {/* Meta row */}
        <div className="mt-6 flex items-center gap-4 text-xs text-slate-500">
          <span><b className="text-slate-800">Date:</b> {data.ended_at ? new Date(data.ended_at).toLocaleDateString() : "—"}</span>
          <span><b className="text-slate-800">Duration:</b> {formatDuration(s.duration_seconds)}</span>
          <span><b className="text-slate-800">Participants:</b> {s.participant_count || 0}</span>
          {data.linked_task && (
            <span className="inline-flex items-center gap-1 text-violet-600 font-medium">
              <LinkIcon size={11} weight="bold" /> Link to Task
            </span>
          )}
        </div>

        {/* KPI grid */}
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="kpi-grid">
          <KpiCard label="Total Blockers" value={s.total_blockers || 0} icon={Warning} tone="rose" />
          <KpiCard label="Done" value={s.done || 0} sub={`${s.done_pct_of_total || 0}% of total`} icon={Check} tone="emerald" />
          <KpiCard label="In Progress" value={s.in_progress || 0} icon={Clock} tone="amber" />
          <KpiCard label="Avg Time Per Issue" value={formatShortTime(s.avg_time_per_issue_seconds)} icon={TrendUp} tone="sky" />
        </div>

        {/* Longest issue + Key contributor */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="rounded-xl bg-white border border-slate-200 p-5" data-testid="longest-issue-card">
            <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Longest Issue</div>
            {s.longest_issue ? (
              <>
                <div className="mt-2 flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{s.longest_issue.text || s.longest_issue.item_id}</div>
                    <div className="mt-1 text-xs text-slate-500">{(s.longest_issue.text || "").length > 60 ? "Complex thread — see notes for details." : "Tracked during the meeting."}</div>
                  </div>
                  {isHost && (
                    <button className="text-slate-400 hover:text-slate-700" data-testid="edit-longest">
                      <PencilSimple size={12} weight="bold" />
                    </button>
                  )}
                </div>
                <div className="mt-3 inline-flex items-center rounded bg-rose-100 text-rose-700 text-[11px] font-bold px-2 py-0.5">
                  Time Impact · {formatShortTime(s.longest_issue.time_impact_seconds)}
                </div>
              </>
            ) : (
              <div className="mt-3 text-xs text-slate-400">No timer activity tracked.</div>
            )}
          </div>

          <KeyContributorCard contributor={s.key_contributor} perOwner={s.per_owner} />
        </div>

        {/* Next Steps + Parking Lot */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-900">Next Steps</h2>
            {isHost && (
              <Button variant="outline" className="h-8 text-xs" data-testid="assign-more-btn">
                Assign More Tasks <ArrowRight size={11} weight="bold" className="ml-1" />
              </Button>
            )}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ListCard title="Next Steps" items={s.next_steps} icon={ListChecks} tone="violet" testid="next-steps-card" />
          <ListCard title="Parking Lot" items={s.parking_lot} icon={Cube} tone="sky" testid="parking-lot-card" />
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, icon: Icon, tone }) {
  const toneCls = {
    rose: "bg-rose-50 text-rose-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    sky: "bg-sky-50 text-sky-700",
  }[tone] || "bg-slate-100 text-slate-700";
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">{label}</div>
        <span className={`grid place-items-center h-7 w-7 rounded-md ${toneCls}`}>
          <Icon size={13} weight="bold" />
        </span>
      </div>
      <div className="mt-2 text-2xl font-bold text-slate-900">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function KeyContributorCard({ contributor, perOwner }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null); // "ach" | "blk"
  const ownerStats = useMemo(() => {
    if (!contributor || !perOwner) return null;
    return perOwner[contributor.email] || null;
  }, [contributor, perOwner]);

  if (!contributor) {
    return (
      <div className="rounded-xl bg-white border border-slate-200 p-5">
        <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Key Contributor</div>
        <div className="mt-3 text-xs text-slate-400">No contributions tracked.</div>
      </div>
    );
  }
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5" data-testid="key-contributor-card">
      <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Key Contributor</div>
      <div className="mt-2 flex items-center gap-3">
        <span className="h-10 w-10 rounded-full text-white text-sm font-bold grid place-items-center" style={{ background: hashGradient(contributor.email) }}>
          {(contributor.email || "?")[0].toUpperCase()}
        </span>
        <div>
          <div className="text-sm font-semibold text-slate-900">{(contributor.email || "").split("@")[0]}</div>
          <div className="text-[11px] text-slate-500 truncate max-w-[200px]">{contributor.email}</div>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2">
        <Popover open={open && mode === "ach"} onOpenChange={(v) => { setOpen(v); setMode(v ? "ach" : null); }}>
          <PopoverTrigger asChild>
            <button className="rounded-md bg-emerald-50 text-emerald-700 text-[11px] font-semibold px-2 py-1 hover:bg-emerald-100" data-testid="achievements-chip">
              <Trophy size={10} weight="fill" className="inline mr-1" />
              {contributor.achievements} Achievements
            </button>
          </PopoverTrigger>
          <PopoverContent className="text-xs">
            <div className="font-semibold mb-1">Achievements</div>
            <div className="text-slate-500">{contributor.achievements} item(s) marked done during this session.</div>
          </PopoverContent>
        </Popover>
        <Popover open={open && mode === "blk"} onOpenChange={(v) => { setOpen(v); setMode(v ? "blk" : null); }}>
          <PopoverTrigger asChild>
            <button className="rounded-md bg-rose-50 text-rose-700 text-[11px] font-semibold px-2 py-1 hover:bg-rose-100" data-testid="blockers-chip">
              <Warning size={10} weight="fill" className="inline mr-1" />
              {contributor.blockers} Blockers
            </button>
          </PopoverTrigger>
          <PopoverContent className="text-xs">
            <div className="font-semibold mb-1">Blockers</div>
            <div className="text-slate-500">{contributor.blockers} blocker(s) flagged in this session.</div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

function ListCard({ title, items, icon: Icon, tone, testid }) {
  const toneCls = {
    violet: "bg-violet-50 text-violet-700",
    sky: "bg-sky-50 text-sky-700",
  }[tone] || "bg-slate-100 text-slate-700";
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">{title} <span className="text-slate-400">({(items || []).length} Items)</span></div>
        <span className={`grid place-items-center h-6 w-6 rounded-md ${toneCls}`}>
          <Icon size={11} weight="bold" />
        </span>
      </div>
      <ul className="mt-3 space-y-2">
        {(items || []).length === 0 && <li className="text-xs text-slate-400">Nothing logged here.</li>}
        {(items || []).map((it) => (
          <li key={it.id} className="text-sm text-slate-700 leading-snug">· {it.text}</li>
        ))}
      </ul>
    </div>
  );
}

function ShareModal({ sessionId }) {
  const [emails, setEmails] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);

  function add() {
    const v = input.trim();
    if (!v) return;
    setEmails((arr) => [...arr, v]);
    setInput("");
  }
  function remove(i) {
    setEmails((arr) => arr.filter((_, idx) => idx !== i));
  }

  async function send() {
    if (emails.length === 0) {
      toast.error("Add an email first");
      return;
    }
    setSending(true);
    try {
      const r = await standupApi.shareSummary(sessionId, { emails });
      toast.success(`Sent to ${r.data.valid.length} teammate${r.data.valid.length === 1 ? "" : "s"}`);
      setEmails([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button className="h-9 bg-violet-600 hover:bg-violet-700 text-white text-xs" data-testid="share-summary-btn">
          <ShareNetwork size={11} weight="bold" className="mr-1" /> Share Meeting Notes
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Send Summary to Teammates</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500">Give your teammates the details on the meeting to support collaboration and communication.</p>

        <div className="mt-3 rounded-lg border border-slate-200 p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-md grid place-items-center bg-teal-100">
              <Cube size={14} weight="bold" className="text-teal-700" />
            </span>
            <div className="text-xs">
              <div className="font-semibold text-slate-900">Meeting link</div>
              <div className="text-slate-500">Copy a direct link to this summary</div>
            </div>
          </div>
          <Button
            variant="outline"
            className="h-8 text-xs"
            onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("Link copied"); }}
            data-testid="copy-summary-link-btn"
          >
            <Copy size={11} weight="bold" className="mr-1" /> Copy link
          </Button>
        </div>

        <div className="my-4 text-center text-[10px] tracking-widest text-slate-400">OR</div>

        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
          placeholder="Enter email addresses…"
          className="h-10 text-sm"
          data-testid="share-email-input"
        />

        {emails.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="share-emails-tags">
            <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase w-full">Results ({emails.length})</div>
            {emails.map((e, i) => {
              const ok = /\S+@\S+\.\S+/.test(e);
              return (
                <span key={i} className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium ${
                  ok ? "bg-sky-100 text-sky-800" : "bg-rose-100 text-rose-700"
                }`}>
                  {e}
                  <button onClick={() => remove(i)} className="opacity-70 hover:opacity-100">
                    <XIcon size={10} weight="bold" />
                  </button>
                </span>
              );
            })}
          </div>
        )}

        <p className="mt-3 text-[11px] text-slate-500">Add emails separated by commas — invalid addresses turn red and won't be sent.</p>

        <div className="mt-5 flex justify-end">
          <Button onClick={send} disabled={sending} className="h-10 px-5 bg-violet-600 hover:bg-violet-700 text-white text-sm" data-testid="send-summary-confirm">
            <PaperPlaneTilt size={11} weight="fill" className="mr-1" /> Send Summary
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function formatDuration(seconds) {
  if (!seconds) return "—";
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}
function formatShortTime(seconds) {
  if (!seconds) return "0m";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m === 0 ? `${s}s` : `${m}m`;
}
function hashGradient(seed) {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) & 0xfff;
  const a = h % 360;
  const b = (a + 60) % 360;
  return `linear-gradient(135deg, hsl(${a}, 70%, 55%), hsl(${b}, 65%, 60%))`;
}
